import React, { useEffect, useRef, useState } from 'react';
import { useAuth, useClerk } from '@clerk/clerk-react';
import {
  ArrowRight,
  BookCopy,
  BookOpen,
  Bot,
  Calculator,
  Check,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Download,
  Instagram,
  Linkedin,
  Menu,
  Mic,
  Quote,
  Smartphone,
  Sparkles,
  StickyNote,
  Target,
  Twitter,
  Users,
  X,
} from 'lucide-react';
import './RefinedAuroraLanding.css';

const CHECKOUT_URL =
  'https://auroraprep.lemonsqueezy.com/buy/1949da6c-f4ce-4551-b443-cab7e24871bb';
const APP_STORE_URL = 'https://apps.apple.com/us/app/aurora-prep/id6760251017';

const features = [
  {
    icon: BookOpen,
    title: 'Problem Solving Lessons',
    description:
      'Learn the strategic methods that consultants at top-tier firms leverage daily.',
    highlights: [
      'Structured frameworks for every case type',
      'Video walkthroughs with expert commentary',
      'Progressive difficulty from beginner to advanced',
    ],
  },
  {
    icon: Calculator,
    title: 'Quant & Math Drills',
    description:
      'Build confidence with targeted quantitative exercises, shortcuts, and in-depth solution walkthroughs.',
    highlights: [
      'Timed exercises simulating real interview pressure',
      'Shortcut techniques for rapid mental math',
      'Step-by-step solution breakdowns for every problem',
    ],
  },
  {
    icon: BookCopy,
    title: 'Practice Case Simulations',
    description:
      'Test your skills and gain instant feedback on how to improve your approach to casing.',
    highlights: [
      'AI-powered instant feedback on your approach',
      'Case library modeled on real consulting interviews',
      'Performance tracking across every attempt',
    ],
  },
  {
    icon: ClipboardList,
    title: 'Resume & Cover Letter Overhaul',
    description:
      'AI-enhanced and human-reviewed feedback to optimize your professional documents for top consulting firms.',
    highlights: [
      'AI analysis paired with human expert review',
      'Industry-specific templates and examples',
      'Targeted feedback for MBB and Big 4 firms',
    ],
  },
  {
    icon: Bot,
    title: '24/7 AI Tutor',
    description:
      'Instant guidance, clarifications, and feedback. Perfect for late-night study or quick concept checks.',
    highlights: [
      'Instant answers to any consulting concept',
      'Personalized guidance based on your progress',
      'Available whenever you need it, day or night',
    ],
  },
  {
    icon: Users,
    title: 'Behavioral Interview Prep',
    description:
      'Structured modules to craft compelling STAR stories and master those tricky “fit” questions.',
    highlights: [
      'STAR method frameworks with real examples',
      'Practice with AI-simulated interviewers',
      'Feedback on delivery, structure, and impact',
    ],
  },
  {
    icon: Target,
    title: 'Goal-Focused Roadmaps',
    description:
      'Customized study plans to keep you on track and align your practice with specific target firms.',
    highlights: [
      'Personalized plans for your target firms',
      'Milestone tracking and adaptive scheduling',
      'Aligned with real recruiting timelines',
    ],
  },
  {
    icon: StickyNote,
    title: 'Noted Integration',
    description:
      'Seamless note-taking and organization across all practice modules for continuous reference.',
    highlights: [
      'Capture insights as you learn across modules',
      'Organized by topic for quick review',
      'Export and share your study notes',
    ],
  },
];

const capabilities = [
  {
    icon: BookCopy,
    eyebrow: 'Interactive cases',
    title: 'Case interview practice',
    href: '#platform-demo',
    description:
      'Work through consulting-style cases, structure your analysis, and review detailed feedback on what worked and what to improve.',
  },
  {
    icon: Mic,
    eyebrow: 'Real-time conversation',
    title: 'Voice AI interviews',
    href: '#voice-ai',
    description:
      'Practice cases out loud in a natural back-and-forth with an AI interviewer—no typing required.',
  },
  {
    icon: Calculator,
    eyebrow: 'Targeted drills',
    title: 'Mental math training',
    href: '#feature-workspace',
    description:
      'Build speed and accuracy with timed consulting math drills, useful shortcuts, and step-by-step solutions.',
  },
  {
    icon: ClipboardList,
    eyebrow: 'AI-guided feedback',
    title: 'Application review',
    href: '#feature-workspace',
    description:
      'Use AI-assisted resume and cover letter feedback to sharpen structure, clarity, and consulting relevance.',
  },
];

const offeringPillars = [
  {
    icon: BookOpen,
    label: 'Learn',
    title: 'Consulting foundations',
    description:
      'Build the core skills behind strong case performance with structured lessons, frameworks, examples, and guided practice.',
    highlights: [
      'Case fundamentals and business concepts',
      'Framework-building guidance',
      'Mental math lessons and drills',
    ],
  },
  {
    icon: Target,
    label: 'Practice',
    title: 'Interview simulations',
    description:
      'Move from passive studying to realistic practice with interactive cases and an interviewer that responds to your approach.',
    highlights: [
      'Voice-to-voice case interviews',
      'Market sizing and strategy cases',
      'Personalized session feedback',
    ],
  },
  {
    icon: ClipboardList,
    label: 'Apply',
    title: 'Application preparation',
    description:
      'Present a sharper consulting application with focused tools for your resume, cover letter, and recruiting strategy.',
    highlights: [
      'Consulting resume feedback',
      'Cover letter recommendations',
      'Application readiness guidance',
    ],
  },
];

const preparationSteps = [
  {
    label: 'Diagnose',
    title: 'Know exactly where you stand.',
    description:
      'Start with a case, math drill, or application review to surface your strengths and the skills that need more work.',
  },
  {
    label: 'Practice',
    title: 'Train with realistic pressure.',
    description:
      'Work through structured cases, timed drills, and voice interviews that require you to think and communicate in the moment.',
  },
  {
    label: 'Improve',
    title: 'Turn feedback into progress.',
    description:
      'Review detailed feedback, revisit the right lessons, and repeat with a clearer focus for your next session.',
  },
];

const outcomes = [
  {
    value: '24/7',
    description: 'Lessons, drills, case practice, and AI guidance when your schedule allows.',
  },
  {
    value: '4-in-1',
    description: 'Cases, mental math, voice practice, and application feedback in one place.',
  },
  {
    value: 'Mobile',
    description: 'Preparation that moves with you through the free Aurora Prep iOS app.',
  },
  {
    value: 'Focused',
    description: 'Feedback, notes, and roadmaps built around the skills that need more work.',
  },
];

const productScenes = [
  {
    label: 'Web platform',
    title: 'Lessons, practice, and progress in one workspace.',
    src: '/images/AuroraPrep_Page1.png',
    alt: 'Aurora Prep web platform showing consulting lessons and performance dashboards',
    layout: 'desktop',
  },
  {
    label: 'Case feedback',
    title: 'Specific feedback after every practice case.',
    src: '/images/app/CASE_RESULTS.PNG',
    secondarySrc: '/images/app/CASE.PNG',
    alt: 'Aurora Prep case feedback with a grade, strengths, and areas to improve',
    secondaryAlt: 'Aurora Prep mobile case study brief',
    layout: 'phones',
  },
  {
    label: 'AI coach',
    title: 'Consulting guidance whenever a question comes up.',
    src: '/images/app/AI_CHAT.PNG',
    secondarySrc: '/images/app/MATH.PNG',
    alt: 'Aurora Prep mobile AI coach answering a consulting question',
    secondaryAlt: 'Aurora Prep mobile mental math practice',
    layout: 'phones',
  },
  {
    label: 'Noted',
    title: 'Keep every framework and interview insight organized.',
    src: '/images/Noted.png',
    alt: 'Noted workspace integrated with Aurora Prep',
    layout: 'desktop',
  },
];

const mobileScreens = [
  { label: 'Home', src: '/images/app/HOME.PNG', alt: 'Aurora Prep mobile home screen' },
  {
    label: 'Case library',
    src: '/images/app/CASE_LIBRARY.PNG',
    alt: 'Aurora Prep mobile case library',
  },
  { label: 'Case study', src: '/images/app/CASE.PNG', alt: 'Aurora Prep mobile case study' },
  { label: 'AI coach', src: '/images/app/AI_CHAT.PNG', alt: 'Aurora Prep mobile AI coach' },
  { label: 'Lessons', src: '/images/app/LESSON.PNG', alt: 'Aurora Prep mobile lesson' },
  { label: 'Math practice', src: '/images/app/MATH.PNG', alt: 'Aurora Prep mobile math practice' },
];

const schoolLogos = [
  { name: 'Wharton', src: '/images/Wharton.png' },
  { name: 'UCLA', src: '/images/UCLA.png' },
  { name: 'Northwestern', src: '/images/Northwestern.png' },
  { name: 'NYU', src: '/images/NYU.png' },
  { name: 'MIT', src: '/images/MIT.png' },
  { name: 'Harvard', src: '/images/Harvard_1.png' },
  { name: 'Chicago Booth', src: '/images/Chicago_1.png' },
  { name: 'OU', src: '/images/OU.png' },
];

const testimonials = [
  {
    quote:
      "Aurora Prep has provided me the frameworks necessary to confidently tackle case study interviews. Whether you're an experienced consultant such as myself or you're entirely new to casing and the consulting industry, there's a lot to learn here. 100% recommend this to anyone looking to prepare for case studies, as well as anyone who is interested in learning more about how business leaders think through key decisions.",
    name: 'Connor',
    detail: "Northwestern (Kellogg) '25",
  },
  {
    quote:
      'Aurora prep is such a valuable resource for learning how to succeed in the case interview. There are so many great tools on the platform. My favorite was the Case Prep Simulations that gave me really useful feedback almost immediately. I would highly recommend Aurora prep to anyone looking to break into consulting!',
    name: 'Jack',
    detail: "UCLA '24",
  },
  {
    quote:
      'Aurora Prep was a great change of pace from just reading books on case prep. The interactive case prep was extremely helpful, and the automated resume reviewer was really insightful as I was trying to craft my resume to standout without having had prior consulting experience. Would recommend to anyone.',
    name: 'Christian',
    detail: 'Consultant at Big 4',
  },
];

const faqs = [
  {
    question: 'Who is Aurora Prep designed for?',
    answer:
      'Aurora Prep is for students, career changers, and experienced professionals preparing for consulting interviews. It supports candidates learning casing for the first time and those sharpening specific skills.',
  },
  {
    question: 'Does Aurora Prep replace a human case partner?',
    answer:
      'Aurora is designed to complement—not fully replace—human practice. It gives you on-demand structure, repetition, and feedback, while a human partner can help you practice with different personalities and interview styles.',
  },
  {
    question: 'Can I practice interviews out loud?',
    answer:
      'Yes. Voice AI lets you speak naturally with an AI interviewer in structured case scenarios, receive real-time responses, and practice without typing.',
  },
  {
    question: 'Is Aurora Prep available on mobile?',
    answer:
      'Yes. The free iOS app includes case studies, a case library, math drills, structured lessons, an AI coach, and progress tracking. Premium web features are available separately.',
  },
  {
    question: 'Can I cancel my subscription?',
    answer:
      'Yes. Premium offers monthly and yearly billing, and you can cancel anytime.',
  },
];

const freePlanFeatures = [
  'Case studies & case library',
  'Math practice drills',
  'AI coach for quick guidance',
  'Structured lessons & frameworks',
  'Study anywhere, anytime on iOS',
  'Track your progress on the go',
];

const premiumPlanFeatures = [
  'AI Real-Time Voice Case Interviews',
  'Resume & Cover Letter Reviewer',
  'Simulated Case Problems with Detailed Feedback',
  'Complete Frameworks & Study Guides',
  'Unlimited Access to the AI Tutor',
  'Free Access to Noted App',
  'Goal-Focused Roadmaps & Study Plans',
];

const Logo = () => (
  <a className="rap-logo" href="#top" aria-label="Aurora Prep home">
    <img src="/images/ap_logo.svg" alt="" />
    <span>Aurora Prep</span>
  </a>
);

const RefinedAuroraLanding = () => {
  const { isSignedIn, getToken } = useAuth();
  const { openSignIn, openSignUp } = useClerk();
  const scrollProgressRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeFeature, setActiveFeature] = useState(0);
  const [activeScene, setActiveScene] = useState(0);
  const [scenePaused, setScenePaused] = useState(false);
  const [activeMobileScreen, setActiveMobileScreen] = useState(0);
  const [mobilePaused, setMobilePaused] = useState(false);
  const [annualBilling, setAnnualBilling] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  const backendUrl =
    process.env.REACT_APP_BACK_END_URL || 'http://localhost:8080';

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  useEffect(() => {
    const site = document.querySelector('.rap-site');
    const header = site?.querySelector('.rap-header');
    let animationFrame;

    const updateScrollState = () => {
      animationFrame = undefined;
      const scrollableHeight = Math.max(
        document.documentElement.scrollHeight - window.innerHeight,
        1
      );
      const progress = Math.min(Math.max(window.scrollY / scrollableHeight, 0), 1);

      if (scrollProgressRef.current) {
        scrollProgressRef.current.style.transform = `scaleX(${progress})`;
      }

      header?.classList.toggle('is-scrolled', window.scrollY > 24);
    };

    const requestScrollUpdate = () => {
      if (animationFrame !== undefined) return;
      animationFrame = window.requestAnimationFrame(updateScrollState);
    };

    updateScrollState();
    window.addEventListener('scroll', requestScrollUpdate, { passive: true });
    window.addEventListener('resize', requestScrollUpdate);

    return () => {
      window.removeEventListener('scroll', requestScrollUpdate);
      window.removeEventListener('resize', requestScrollUpdate);
      if (animationFrame !== undefined) window.cancelAnimationFrame(animationFrame);
    };
  }, []);

  useEffect(() => {
    const site = document.querySelector('.rap-site');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    if (!site || reducedMotion.matches) return undefined;

    const root = document.documentElement;
    const sections = Array.from(site.querySelectorAll('main > section:not(.rap-hero)'));
    const revealSelector = [
      '.rap-section-heading',
      '.rap-capability-card',
      '.rap-offerings-grid article',
      '.rap-platform-video-wrap',
      '.rap-method-grid article',
      '.rap-voice-media',
      '.rap-voice-copy',
      '.rap-mobile-copy',
      '.rap-mobile-showcase',
      '.rap-explorer-intro',
      '.rap-feature-explorer',
      '.rap-noted-copy',
      '.rap-noted-image',
      '.rap-outcomes-quote',
      '.rap-outcomes-grid article',
      '.rap-pricing-heading',
      '.rap-billing-toggle',
      '.rap-price-card',
      '.rap-people-heading',
      '.rap-person-card',
      '.rap-mission-grid article',
      '.rap-testimonial-heading',
      '.rap-testimonial-grid article',
      '.rap-faq-heading',
      '.rap-faq-list',
      '.rap-final-cta-inner > *',
    ].join(',');

    sections.forEach((section) => {
      section.classList.add('rap-reveal-section');

      Array.from(section.querySelectorAll(revealSelector)).forEach((item, index) => {
        item.classList.add('rap-scroll-item');
        item.style.setProperty('--rap-reveal-delay', `${Math.min(index, 6) * 75}ms`);

        if (
          item.matches(
            '.rap-platform-video-wrap, .rap-voice-media, .rap-mobile-showcase, .rap-feature-explorer, .rap-noted-image'
          )
        ) {
          item.classList.add('rap-scroll-media');
        }
      });
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      {
        rootMargin: '0px 0px -12% 0px',
        threshold: 0.08,
      }
    );

    sections.forEach((section) => observer.observe(section));
    root.classList.add('rap-smooth-scroll');
    window.requestAnimationFrame(() => site.classList.add('rap-motion-enabled'));

    return () => {
      observer.disconnect();
      root.classList.remove('rap-smooth-scroll');
      site.classList.remove('rap-motion-enabled');
      sections.forEach((section) => {
        section.classList.remove('rap-reveal-section', 'is-visible');
        section.querySelectorAll('.rap-scroll-item').forEach((item) => {
          item.classList.remove('rap-scroll-item', 'rap-scroll-media');
          item.style.removeProperty('--rap-reveal-delay');
        });
      });
    };
  }, []);

  useEffect(() => {
    if (scenePaused || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setActiveScene((current) => (current + 1) % productScenes.length);
    }, 5200);

    return () => window.clearInterval(timer);
  }, [scenePaused]);

  useEffect(() => {
    if (mobilePaused || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setActiveMobileScreen((current) => (current + 1) % mobileScreens.length);
    }, 4200);

    return () => window.clearInterval(timer);
  }, [mobilePaused]);

  const continueToProduct = async () => {
    setLoading(true);
    setError('');

    try {
      const token = await getToken();
      const response = await fetch(backendUrl + '/api/offerings', {
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + token,
        },
      });

      if (!response.ok) throw new Error('Unable to check account access');

      const data = await response.json();
      window.location.href = data.payingCustomer ? '/main' : CHECKOUT_URL;
    } catch (requestError) {
      console.error('Access check failed:', requestError);
      setError('We could not check your account. Please try again.');
      setLoading(false);
    }
  };

  const handleGetStarted = () => {
    setMenuOpen(false);
    if (!isSignedIn) {
      openSignUp();
      return;
    }
    continueToProduct();
  };

  const handleLogin = () => {
    setMenuOpen(false);
    if (!isSignedIn) {
      openSignIn();
      return;
    }
    continueToProduct();
  };

  const buttonLabel = loading
    ? 'Checking access…'
    : isSignedIn
      ? 'Continue to Aurora'
      : 'Get started';

  const selectedFeature = features[activeFeature];
  const SelectedFeatureIcon = selectedFeature.icon;
  const activeMobile = mobileScreens[activeMobileScreen];

  return (
    <div className="rap-site" id="top">
      <header className="rap-header">
        <div className="rap-shell rap-nav">
          <Logo />

          <nav className="rap-nav-links" aria-label="Main navigation">
            <a href="#platform-demo">Platform</a>
            <a href="#offerings">Offerings</a>
            <a href="#method">Method</a>
            <a href="#mobile-app">Mobile app</a>
            <a href="#pricing">Pricing</a>
          </nav>

          <div className="rap-nav-actions">
            <button className="rap-text-button" type="button" onClick={handleLogin}>
              {isSignedIn ? 'My account' : 'Log in'}
            </button>
            <button
              className="rap-button rap-button-small"
              type="button"
              onClick={handleGetStarted}
              disabled={loading}
            >
              {isSignedIn ? 'Continue' : 'Get started'}
              <ArrowRight size={16} />
            </button>
          </div>

          <button
            className="rap-menu-button"
            type="button"
            onClick={() => setMenuOpen((current) => !current)}
            aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={menuOpen}
          >
            {menuOpen ? <X /> : <Menu />}
          </button>
        </div>

        {menuOpen && (
          <div className="rap-mobile-menu">
            <a href="#platform-demo" onClick={() => setMenuOpen(false)}>Platform</a>
            <a href="#offerings" onClick={() => setMenuOpen(false)}>Offerings</a>
            <a href="#method" onClick={() => setMenuOpen(false)}>Method</a>
            <a href="#mobile-app" onClick={() => setMenuOpen(false)}>Mobile app</a>
            <a href="#pricing" onClick={() => setMenuOpen(false)}>Pricing</a>
            <button type="button" onClick={handleLogin}>
              {isSignedIn ? 'My account' : 'Log in'}
            </button>
            <button className="rap-button" type="button" onClick={handleGetStarted}>
              {buttonLabel}
            </button>
          </div>
        )}
        <span className="rap-scroll-progress" ref={scrollProgressRef} aria-hidden="true" />
      </header>

      <main>
        <section className="rap-hero">
          <div className="rap-hero-grid-overlay" aria-hidden="true" />
          <div className="rap-hero-orb rap-hero-orb-one" aria-hidden="true" />
          <div className="rap-hero-orb rap-hero-orb-two" aria-hidden="true" />

          <div className="rap-shell rap-hero-grid">
            <div className="rap-hero-copy">
              <p className="rap-eyebrow">
                <Sparkles size={15} />
                The complete consulting interview workspace
              </p>
              <h1>
                <span className="rap-hero-line">Prepare with</span>
                <span className="rap-hero-accent">clarity.</span>
                <span className="rap-hero-line">Interview with confidence.</span>
              </h1>
              <p className="rap-hero-description">
                A focused preparation platform for candidates pursuing management
                consulting. Master cases, sharpen your math, improve your
                application, and practice live with Voice AI.
              </p>

              <div className="rap-hero-actions">
                <button
                  className="rap-button rap-button-large"
                  type="button"
                  onClick={handleGetStarted}
                  disabled={loading}
                >
                  {buttonLabel}
                  <ArrowRight size={18} />
                </button>
                <a className="rap-secondary-button" href="#offerings">
                  Explore the offerings
                </a>
              </div>

              {error && <p className="rap-error" role="alert">{error}</p>}

              <div className="rap-hero-meta">
                <span><Check size={14} /> Free iOS app</span>
                <span><Check size={14} /> Premium web platform</span>
                <span><Check size={14} /> Cancel anytime</span>
              </div>
            </div>

            <div
              className="rap-product-reel"
              onMouseEnter={() => setScenePaused(true)}
              onMouseLeave={() => setScenePaused(false)}
              onFocusCapture={() => setScenePaused(true)}
              onBlurCapture={() => setScenePaused(false)}
            >
              <video
                className="rap-reel-background"
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
                aria-hidden="true"
              >
                <source src="/images/nyc_hero_bg.mp4" type="video/mp4" />
              </video>
              <div className="rap-reel-tint" aria-hidden="true" />

              <div className="rap-reel-shell">
                <div className="rap-reel-topbar">
                  <span><i /> Real Aurora Prep product</span>
                  <strong>{String(activeScene + 1).padStart(2, '0')} / {String(productScenes.length).padStart(2, '0')}</strong>
                </div>

                <div className="rap-scene-viewport">
                  {productScenes.map((scene, index) => (
                    <figure
                      className={'rap-scene rap-scene-' + scene.layout + (index === activeScene ? ' is-active' : '')}
                      key={scene.label}
                      aria-hidden={index !== activeScene}
                    >
                      {scene.layout === 'phones' ? (
                        <div className="rap-scene-phone-stack">
                          <div className="rap-scene-phone rap-scene-phone-back">
                            <img src={scene.secondarySrc} alt={scene.secondaryAlt} />
                          </div>
                          <div className="rap-scene-phone rap-scene-phone-front">
                            <img src={scene.src} alt={scene.alt} />
                          </div>
                        </div>
                      ) : (
                        <div className="rap-scene-browser">
                          <div className="rap-browser-chrome" aria-hidden="true">
                            <span /><span /><span />
                            <i>auroraprep.com</i>
                          </div>
                          <img src={scene.src} alt={scene.alt} />
                        </div>
                      )}
                    </figure>
                  ))}
                </div>

                <div className="rap-reel-footer">
                  <div>
                    <span>{productScenes[activeScene].label}</span>
                    <strong>{productScenes[activeScene].title}</strong>
                  </div>
                  <div className="rap-reel-dots" aria-label="Choose a product preview">
                    {productScenes.map((scene, index) => (
                      <button
                        key={scene.label}
                        type="button"
                        className={index === activeScene ? 'is-active' : ''}
                        onClick={() => setActiveScene(index)}
                        aria-label={'Show ' + scene.label}
                        aria-pressed={index === activeScene}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="rap-trust-strip">
            <div className="rap-shell rap-trust-layout">
              <p>Trusted by students from</p>
              <div className="rap-logo-marquee" aria-label="Schools represented in the Aurora Prep community">
                <div className="rap-logo-track">
                  {[...schoolLogos, ...schoolLogos].map((school, index) => (
                    <img
                      key={school.name + index}
                      src={school.src}
                      alt={index < schoolLogos.length ? school.name : ''}
                      aria-hidden={index >= schoolLogos.length}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="rap-features rap-shell" id="features">
          <div className="rap-section-heading rap-section-heading-split">
            <div>
              <p className="rap-kicker">One preparation system</p>
              <h2>Everything you need to become interview-ready.</h2>
            </div>
            <p>
              Stop piecing together disconnected videos, spreadsheets, and case
              books. Aurora Prep brings your preparation into one considered workspace.
            </p>
          </div>

          <div className="rap-capability-grid">
            {capabilities.map((capability, index) => {
              const Icon = capability.icon;
              return (
                <article className="rap-capability-card" key={capability.title}>
                  <div className="rap-capability-meta">
                    <span>{String(index + 1).padStart(2, '0')}</span>
                    <Icon size={24} />
                  </div>
                  <div>
                    <p className="rap-kicker">{capability.eyebrow}</p>
                    <h3>{capability.title}</h3>
                    <p>{capability.description}</p>
                  </div>
                  <a className="rap-inline-link" href={capability.href}>
                    Learn more <ArrowRight size={17} />
                  </a>
                </article>
              );
            })}
          </div>
        </section>

        <section className="rap-offerings" id="offerings">
          <div className="rap-shell">
            <div className="rap-section-heading rap-section-heading-split">
              <div>
                <p className="rap-kicker">What Aurora offers</p>
                <h2>One membership. Your complete recruiting toolkit.</h2>
              </div>
              <p>
                Aurora Prep supports the full path from learning consulting
                fundamentals to practicing live interviews and submitting a
                polished application.
              </p>
            </div>

            <div className="rap-offerings-grid">
              {offeringPillars.map((offering) => {
                const Icon = offering.icon;
                return (
                  <article key={offering.title}>
                    <span className="rap-offering-icon"><Icon size={24} /></span>
                    <p className="rap-kicker">{offering.label}</p>
                    <h3>{offering.title}</h3>
                    <p>{offering.description}</p>
                    <ul>
                      {offering.highlights.map((highlight) => (
                        <li key={highlight}><Check size={16} />{highlight}</li>
                      ))}
                    </ul>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="rap-platform" id="platform-demo">
          <div className="rap-shell">
            <div className="rap-section-heading rap-section-heading-split rap-platform-heading">
              <div>
                <p className="rap-kicker">The platform and people behind it</p>
                <h2>See Aurora Prep in action. Meet the experts shaping it.</h2>
              </div>
              <p>
                Aurora Prep combines practical consulting experience, expert
                guidance, and interactive technology in one focused preparation platform.
              </p>
            </div>

            <div className="rap-platform-video-wrap">
              <div className="rap-video-label">
                <span><i /> Product tour</span>
                <strong>Learn · Practice · Improve</strong>
              </div>
              <video
                controls
                playsInline
                preload="metadata"
                poster="/images/AuroraPrep_Page1.png"
                aria-label="Aurora Prep platform demonstration"
              >
                <source src="/images/auroraprep.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </section>

        <section className="rap-method" id="method">
          <div className="rap-shell">
            <div className="rap-section-heading rap-section-heading-split">
              <div>
                <p className="rap-kicker">A deliberate method</p>
                <h2>Practice should feel like progress.</h2>
              </div>
              <p>
                Every part of Aurora Prep follows a simple loop: understand your
                gaps, practice deliberately, and use feedback to improve.
              </p>
            </div>

            <div className="rap-method-grid">
              {preparationSteps.map((step, index) => (
                <article key={step.label}>
                  <span className="rap-method-number">{String(index + 1).padStart(2, '0')}</span>
                  <div>
                    <p className="rap-kicker">{step.label}</p>
                    <h3>{step.title}</h3>
                    <p>{step.description}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="rap-voice rap-shell" id="voice-ai">
          <div className="rap-voice-media">
            <video
              controls
              playsInline
              preload="metadata"
              poster="/images/AuroraPrep_Page1.png"
              aria-label="Aurora Prep voice case interview demonstration"
            >
              <source src="/images/voiceConsulting.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            <div className="rap-voice-badge"><Mic size={15} /> Voice-to-voice</div>
          </div>

          <div className="rap-voice-copy">
            <p className="rap-kicker"><span>New</span> Voice AI interviews</p>
            <h2>A case partner ready whenever you are.</h2>
            <p>
              Practice communicating your thinking—not just writing it down.
              Aurora’s Voice AI responds in real time, asks follow-up questions,
              and gives you space to lead the conversation.
            </p>
            <ul>
              <li><Check size={17} />Natural back-and-forth dialogue with an AI interviewer</li>
              <li><Check size={17} />Real-time voice input and response—no typing required</li>
              <li><Check size={17} />Structured case scenarios designed to mirror top-firm interviews</li>
            </ul>
            <button className="rap-inline-link" type="button" onClick={handleGetStarted}>
              Try voice case interviews <ArrowRight size={17} />
            </button>
          </div>
        </section>

        <section className="rap-mobile" id="mobile-app">
          <div className="rap-shell rap-mobile-grid">
            <div className="rap-mobile-copy">
              <p className="rap-kicker rap-kicker-light"><Smartphone size={15} /> Free on the App Store</p>
              <h2>Prep anywhere. Anytime.</h2>
              <p>
                Squeeze in a case study on your commute, drill math problems
                between classes, or chat with your AI coach from anywhere.
                Aurora Prep Mobile puts serious consulting prep in your pocket—completely free.
              </p>
              <ul>
                <li><Check size={17} />Practice case studies on the train or between classes</li>
                <li><Check size={17} />Quick math drills whenever you have 5 minutes</li>
                <li><Check size={17} />AI coach available 24/7 right in your pocket</li>
                <li><Check size={17} />Track your progress and pick up where you left off</li>
              </ul>
              <a className="rap-light-button" href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
                <Download size={17} /> Download free on the App Store
              </a>
            </div>

            <div
              className="rap-mobile-showcase"
              onMouseEnter={() => setMobilePaused(true)}
              onMouseLeave={() => setMobilePaused(false)}
              onFocusCapture={() => setMobilePaused(true)}
              onBlurCapture={() => setMobilePaused(false)}
            >
              <div className="rap-phone-orbit rap-phone-orbit-one" aria-hidden="true" />
              <div className="rap-phone-orbit rap-phone-orbit-two" aria-hidden="true" />
              <div className="rap-phone-ghost rap-phone-ghost-left" aria-hidden="true">
                <img src="/images/app/CASE_LIBRARY.PNG" alt="" />
              </div>
              <figure className="rap-phone-active" key={activeMobile.src}>
                <div className="rap-phone-speaker" aria-hidden="true" />
                <img src={activeMobile.src} alt={activeMobile.alt} />
                <figcaption>{activeMobile.label}</figcaption>
              </figure>
              <div className="rap-phone-ghost rap-phone-ghost-right" aria-hidden="true">
                <img src="/images/app/AI_CHAT.PNG" alt="" />
              </div>

              <div className="rap-mobile-controls">
                <button
                  type="button"
                  aria-label="Previous app screen"
                  onClick={() => setActiveMobileScreen((current) => (current - 1 + mobileScreens.length) % mobileScreens.length)}
                >
                  <ChevronLeft size={18} />
                </button>
                <div>
                  {mobileScreens.map((screen, index) => (
                    <button
                      key={screen.label}
                      type="button"
                      className={index === activeMobileScreen ? 'is-active' : ''}
                      aria-label={'Show ' + screen.label}
                      aria-pressed={index === activeMobileScreen}
                      onClick={() => setActiveMobileScreen(index)}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  aria-label="Next app screen"
                  onClick={() => setActiveMobileScreen((current) => (current + 1) % mobileScreens.length)}
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className="rap-feature-workspace rap-shell" id="feature-workspace">
          <div className="rap-explorer-intro">
            <p className="rap-kicker">Explore every tool</p>
            <h2>A complete workspace for focused preparation.</h2>
          </div>

          <div className="rap-feature-explorer">
            <div className="rap-feature-tabs" role="tablist" aria-label="Aurora Prep features">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                const selected = index === activeFeature;
                return (
                  <button
                    key={feature.title}
                    type="button"
                    role="tab"
                    aria-selected={selected}
                    aria-controls="rap-feature-panel"
                    className={selected ? 'is-active' : ''}
                    onClick={() => setActiveFeature(index)}
                  >
                    <span className="rap-feature-tab-icon"><Icon size={19} /></span>
                    <span>{feature.title}</span>
                    <small>{String(index + 1).padStart(2, '0')}</small>
                  </button>
                );
              })}
            </div>

            <div className="rap-feature-panel" id="rap-feature-panel" role="tabpanel">
              <div className="rap-feature-panel-number">
                {String(activeFeature + 1).padStart(2, '0')}
              </div>
              <div className="rap-feature-panel-icon"><SelectedFeatureIcon size={28} /></div>
              <p className="rap-kicker">Included with premium</p>
              <h3>{selectedFeature.title}</h3>
              <p>{selectedFeature.description}</p>
              <ul>
                {selectedFeature.highlights.map((highlight) => (
                  <li key={highlight}><Check size={16} />{highlight}</li>
                ))}
              </ul>
              <button className="rap-inline-link" type="button" onClick={handleGetStarted}>
                Start preparing <ArrowRight size={17} />
              </button>
            </div>
          </div>
        </section>

        <section className="rap-noted rap-shell" id="noted">
          <div className="rap-noted-copy">
            <p className="rap-kicker"><StickyNote size={15} /> Noted integration</p>
            <h2>Seamless notes with Noted.</h2>
            <p>
              Organize all your insights, frameworks, and interview strategies in
              one place. Access and update your notes in real time without ever
              leaving the Aurora Prep platform.
            </p>
            <div className="rap-noted-quote">
              “You don’t have any notes yet. Start practicing to create your first note!”
            </div>
          </div>
          <div className="rap-noted-image">
            <div className="rap-browser-chrome" aria-hidden="true">
              <span /><span /><span />
              <i>Noted by Aurora Prep</i>
            </div>
            <img src="/images/Noted.png" alt="Noted workspace for organizing consulting interview notes" />
          </div>
        </section>

        <section className="rap-outcomes">
          <div className="rap-shell rap-outcomes-layout">
            <div className="rap-outcomes-quote">
              <p className="rap-kicker">Designed for real progress</p>
              <blockquote>
                “Preparation becomes much easier when every session tells you
                exactly what to work on next.”
              </blockquote>
              <span>Aurora Prep learning philosophy</span>
            </div>

            <div className="rap-outcomes-grid">
              {outcomes.map((outcome) => (
                <article key={outcome.value}>
                  <strong>{outcome.value}</strong>
                  <p>{outcome.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="rap-pricing" id="pricing">
          <div className="rap-shell">
            <div className="rap-section-heading rap-section-heading-split rap-pricing-heading">
              <div>
                <p className="rap-kicker">Simple access</p>
                <h2>Serious preparation without the coaching price tag.</h2>
              </div>
              <p>
                Start free with the mobile app, or unlock the full web platform
                for live practice, detailed feedback, and application support.
              </p>
            </div>

            <div className="rap-billing-toggle" aria-label="Premium billing period">
              <button
                type="button"
                className={!annualBilling ? 'is-active' : ''}
                onClick={() => setAnnualBilling(false)}
              >
                Monthly
              </button>
              <button
                className="rap-switch"
                type="button"
                role="switch"
                aria-checked={annualBilling}
                aria-label="Use yearly billing"
                onClick={() => setAnnualBilling((current) => !current)}
              >
                <span />
              </button>
              <button
                type="button"
                className={annualBilling ? 'is-active' : ''}
                onClick={() => setAnnualBilling(true)}
              >
                Yearly
              </button>
            </div>

            <div className="rap-price-grid">
              <article className="rap-price-card rap-price-card-free">
                <div className="rap-price-card-heading">
                  <span className="rap-price-icon"><Smartphone size={20} /></span>
                  <div>
                    <p>Perfect for prepping on the go</p>
                    <h3>Aurora Prep Mobile</h3>
                  </div>
                </div>
                <div className="rap-price-value"><strong>Free</strong></div>
                <ul>
                  {freePlanFeatures.map((item) => <li key={item}><Check size={16} />{item}</li>)}
                </ul>
                <a className="rap-secondary-button rap-price-action" href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
                  <Download size={17} /> Download for iOS
                </a>
                <p className="rap-price-note">Available now on the App Store.</p>
              </article>

              <article className="rap-price-card rap-price-card-premium">
                <div className="rap-recommended">Recommended</div>
                <div className="rap-price-card-heading">
                  <span className="rap-price-icon"><Sparkles size={20} /></span>
                  <div>
                    <p>Full web platform for comprehensive prep</p>
                    <h3>Premium Platform</h3>
                  </div>
                </div>
                <div className="rap-price-value">
                  <strong>${annualBilling ? '150' : '45'}</strong>
                  <span>/{annualBilling ? 'year' : 'month'}</span>
                </div>
                <p className="rap-included-label">Everything in the free app, plus:</p>
                <ul>
                  {premiumPlanFeatures.map((item) => <li key={item}><Check size={16} />{item}</li>)}
                </ul>
                <button
                  className="rap-button rap-button-full"
                  type="button"
                  onClick={handleGetStarted}
                  disabled={loading}
                >
                  {buttonLabel}<ArrowRight size={17} />
                </button>
                <p className="rap-price-note">Cancel anytime. No long-term commitment.</p>
              </article>
            </div>

            <p className="rap-enterprise-note">
              Looking for enterprise or university access?{' '}
              <a href="mailto:support@auroraprep.com">Contact support@auroraprep.com</a>.
            </p>
          </div>
        </section>

        <section className="rap-people" id="story">
          <div className="rap-shell">
            <div className="rap-section-heading rap-section-heading-split rap-people-heading">
              <div>
                <p className="rap-kicker">The people behind Aurora</p>
                <h2>Built with consulting experience and a clear mission.</h2>
              </div>
              <p>
                Transforming consulting interview preparation through innovation
                and accessibility.
              </p>
            </div>

            <div className="rap-people-grid">
              <article className="rap-person-card rap-person-natalie">
                <img src="/images/CorpNataliexAuroraPrep.png" alt="Corporate Natalie, Strategic Advisor for Aurora Prep" />
                <div className="rap-person-overlay">
                  <span>Strategic Advisor</span>
                  <h3>Corporate Natalie</h3>
                  <p>
                    A former Deloitte consultant and respected thought leader,
                    Natalie helps Aurora Prep transform how students at all levels
                    prepare for interviews.
                  </p>
                </div>
              </article>

              <article className="rap-person-card rap-person-sydney">
                <img src="/images/syd_p.png" alt="Sydney Henriques, CEO and Co-Founder of Aurora Prep" />
                <div className="rap-person-overlay">
                  <span>CEO &amp; Co-Founder</span>
                  <h3>Sydney Henriques</h3>
                  <p>
                    Aurora Prep combines industry expertise with interactive
                    learning and real-time feedback to make effective preparation
                    more accessible and affordable.
                  </p>
                </div>
              </article>
            </div>

            <div className="rap-mission-grid">
              <article>
                <span>Our partnership</span>
                <p>
                  “We’re looking forward to having Natalie as part of the Aurora Prep
                  team as we continue to make our platform the go-to choice for
                  aspiring consultants.”
                </p>
              </article>
              <article>
                <span>Our mission</span>
                <p>
                  Our goal is to cut the traditional 80–100 hour preparation journey
                  to 40 through interactive learning and real-time feedback—so every
                  practice session moves you forward.
                </p>
              </article>
              <article>
                <span>Our vision</span>
                <p>
                  Whether your goal is a top-tier global firm or a small boutique,
                  Aurora Prep was made to help you stand out in the interview.
                </p>
              </article>
            </div>
          </div>
        </section>

        <section className="rap-testimonials">
          <div className="rap-shell">
            <div className="rap-section-heading rap-testimonial-heading">
              <p className="rap-kicker">Candidate stories</p>
              <h2>What our users say.</h2>
            </div>
            <div className="rap-testimonial-grid">
              {testimonials.map((testimonial) => (
                <article key={testimonial.name}>
                  <Quote size={28} />
                  <blockquote>{testimonial.quote}</blockquote>
                  <div>
                    <strong>{testimonial.name}</strong>
                    <span>{testimonial.detail}</span>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="rap-faq" id="faq">
          <div className="rap-shell rap-faq-layout">
            <div className="rap-faq-heading">
              <p className="rap-kicker">Common questions</p>
              <h2>A few things you may want to know.</h2>
            </div>

            <div className="rap-faq-list">
              {faqs.map((faq, index) => (
                <article className={openFaq === index ? 'is-open' : ''} key={faq.question}>
                  <button
                    type="button"
                    aria-expanded={openFaq === index}
                    onClick={() => setOpenFaq((current) => current === index ? -1 : index)}
                  >
                    <span>{faq.question}</span>
                    <ChevronRight size={21} />
                  </button>
                  {openFaq === index && <p>{faq.answer}</p>}
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="rap-final-cta">
          <div className="rap-shell rap-final-cta-inner">
            <div>
              <p className="rap-kicker rap-kicker-light">Your next step</p>
              <h2>Prepare for the offer—not just the interview.</h2>
              <p>Build the skills, confidence, and application to move forward.</p>
            </div>
            <button
              className="rap-light-button rap-light-button-large"
              type="button"
              onClick={handleGetStarted}
              disabled={loading}
            >
              {isSignedIn ? 'Continue to Aurora' : 'Sign up today'}
              <ArrowRight size={18} />
            </button>
          </div>
        </section>
      </main>

      <footer className="rap-footer">
        <div className="rap-shell rap-footer-top">
          <div className="rap-footer-brand">
            <Logo />
            <p>Practical, accessible preparation for aspiring consultants.</p>
          </div>
          <div className="rap-footer-links">
            <div>
              <strong>Platform</strong>
              <a href="#features">Features</a>
              <a href="#platform-demo">Product tour</a>
              <a href="#voice-ai">Voice AI</a>
              <a href="#mobile-app">Mobile app</a>
            </div>
            <div>
              <strong>Prepare</strong>
              <a href="#offerings">Offerings</a>
              <a href="#method">Our method</a>
              <a href="#pricing">Pricing</a>
              <a href="#faq">FAQ</a>
            </div>
            <div>
              <strong>Company</strong>
              <a href="#story">Our story</a>
              <a href="mailto:support@auroraprep.com">Contact</a>
              <a href="/privacy">Privacy</a>
              <a href="/terms">Terms</a>
            </div>
          </div>
        </div>
        <div className="rap-shell rap-footer-bottom">
          <span>© 2026 Aurora Prep. All rights reserved.</span>
          <div className="rap-social-links">
            <a href="https://www.linkedin.com/company/aurora-prep/" target="_blank" rel="noopener noreferrer" aria-label="Aurora Prep on LinkedIn"><Linkedin size={18} /></a>
            <a href="https://x.com/Aurora_Prep" target="_blank" rel="noopener noreferrer" aria-label="Aurora Prep on X"><Twitter size={18} /></a>
            <a href="https://www.instagram.com/auroraprep/" target="_blank" rel="noopener noreferrer" aria-label="Aurora Prep on Instagram"><Instagram size={18} /></a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default RefinedAuroraLanding;
